import { computed, ref } from 'vue'
import { api, type NolsFileInfoDto, type PaginatedResponseDtoOfNolsFileInfoDto } from '@/api/eOrdersApi'
import { useApiAdapter } from '@/store'

type LoadingStatus = 'idle' | 'pending' | 'success' | 'error'

export interface NolsImportFilters {
  search?: string
}

const fallbackEmptyResponse: PaginatedResponseDtoOfNolsFileInfoDto = {
  pageSize: 0,
  currentPage: 0,
  totalItems: 0,
  totalPages: 0,
  data: []
}

export const useNolsImportData = () => {
  const apiAdapter = useApiAdapter()

  const rawData = ref<PaginatedResponseDtoOfNolsFileInfoDto | null>(null)
  const status = ref<LoadingStatus>('idle')
  const error = ref<string | null>(null)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const currentFilters = ref<NolsImportFilters>({})

  const data = computed<NolsFileInfoDto[]>(() => {
    if (!rawData.value?.data) return []

    const items = Array.isArray(rawData.value.data) ? rawData.value.data : []

    return [...items].sort((a, b) => {
      return new Date(b.createdUtc).getTime() - new Date(a.createdUtc).getTime()
    })
  })

  const pagination = computed(() => {
    if (!rawData.value) return null

    return {
      pageSize: rawData.value.pageSize ?? pageSize.value,
      currentPage: rawData.value.currentPage ?? currentPage.value,
      totalItems: rawData.value.totalItems ?? 0,
      totalPages: rawData.value.totalPages ?? 0
    }
  })

  const fetchFiles = async (size: number = pageSize.value, page: number = currentPage.value, filters: NolsImportFilters = currentFilters.value, suppressStatusChange = false) => {
    try {
      if (!suppressStatusChange) {
        status.value = 'pending'
      }
      error.value = null

      const filesFilter = filters.search?.trim() ? [`*${filters.search.trim()}*`] : undefined

      const response = await apiAdapter.invoke(config =>
        api.nolsDownloadList(page, size, undefined, undefined, filesFilter, config)
      )

      if (!response) {
        throw new Error('Failed to load NOLS files.')
      }

      rawData.value = response as PaginatedResponseDtoOfNolsFileInfoDto
      status.value = 'success'
      currentPage.value = page
      pageSize.value = size
      currentFilters.value = filters
    } catch (err) {
      console.error('Failed to fetch NOLS files', err)
      status.value = 'error'
      error.value = err instanceof Error ? err.message : 'Unknown error'
      rawData.value = {
        ...fallbackEmptyResponse,
        pageSize: size,
        currentPage: page
      }
    }
  }

  const applyFilters = async (filters: NolsImportFilters) => {
    currentFilters.value = filters
    currentPage.value = 1
    await fetchFiles(pageSize.value, 1, filters, true)
  }

  const goToPage = async (page: number) => {
    if (page <= 0 || page === currentPage.value) return
    await fetchFiles(pageSize.value, page, currentFilters.value)
  }

  const changePageSize = async (newSize: number) => {
    if (newSize === pageSize.value) return
    await fetchFiles(newSize, 1, currentFilters.value)
  }

  return {
    data,
    status,
    error,
    pagination,
    fetchFiles,
    applyFilters,
    goToPage,
    changePageSize
  }
}
